import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Heart, Phone } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { resolveImage } from "@/lib/media";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { rupees } from "@/lib/format";

export default function CommunityPost() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { t, auth } = useApp();
  const [post, setPost] = useState(null);
  const [liked, setLiked] = useState(false);
  const [draft, setDraft] = useState("");

  const load = async () => {
    const { data } = await api.get(`/community/posts/${postId}`);
    setPost(data);
    if (auth) {
      const res = await api.get(`/community/posts/${postId}/liked`);
      setLiked(res.data.liked);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, auth]);

  const like = async () => {
    if (!auth) {
      navigate("/login");
      return;
    }
    const { data } = await api.post(`/community/posts/${postId}/like`);
    setLiked(data.liked);
    setPost((p) => ({ ...p, like_count: data.like_count }));
  };

  const comment = async () => {
    if (!auth) {
      navigate("/login");
      return;
    }
    try {
      await api.post(`/community/posts/${postId}/comments`, { body: draft });
      setDraft("");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  if (!post) return <div className="p-10 text-muted-foreground">{t("common.loading")}</div>;

  const typeLabels = t("community.typeLabels");

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8" data-testid="community-post-page">
      <Link to="/community" className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="back-to-board">
        <ArrowLeft size={14} /> {t("community.backToBoard")}
      </Link>

      <Badge variant="outline" className="mt-6">
        {typeLabels[post.post_type]}
      </Badge>
      <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight">{post.title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{post.author_name}</p>
      {post.price ? <p className="mt-2 font-mono text-xl">{rupees({ minor_units: post.price?.minor_units })}</p> : null}
      {post.image_url && (
        <img src={resolveImage(post.image_url)} alt="" className="mt-5 w-full rounded-2xl object-cover" />
      )}
      <p className="mt-5 leading-relaxed text-muted-foreground">{post.body}</p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button
          variant={liked ? "default" : "outline"}
          size="sm"
          className="rounded-full"
          data-testid="post-like-btn"
          onClick={like}
        >
          <Heart size={14} className="mr-1" /> {post.like_count}
        </Button>
        {post.contact_phone && (
          <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="post-call-btn">
            <a href={`tel:${post.contact_phone}`}>
              <Phone size={14} className="mr-1" /> {t("common.call")}
            </a>
          </Button>
        )}
      </div>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold">
          {t("community.comments")} · {post.comments.length}
        </h2>
        <div className="mt-4 space-y-3" data-testid="post-comments">
          {post.comments.map((c) => (
            <div key={c.id} className="rounded-xl border border-border p-4" data-testid={`comment-${c.id}`}>
              <p className="text-sm font-medium">{c.author_name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{c.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <Input
            data-testid="comment-input"
            value={draft}
            placeholder={t("community.commentPlaceholder")}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button className="rounded-full" data-testid="comment-submit-btn" onClick={comment}>
            {t("community.addComment")}
          </Button>
        </div>
      </section>
    </div>
  );
}
